import React, { useEffect, useState } from 'react';
import { fetchPaginatedData } from '../api/requestService';
import './TableViewer.css';
import TableFilterBar from './TableFilterBar'; 

import AddFormModal from './AddFormModal';
import { addTableRow } from '../api/requestService';
import { deleteTableRow } from '../api/requestService';


const TableViewer = ({ prefix, tableName, columns, title, filters = [], creatable = false, addEndpoint, deletable = false, deleteEndpoint }) => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [finalSearchTerm, setFinalSearchTerm] = useState('');

  const [filterQuery, setFilterQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({});

  const [showAddModal, setShowAddModal] = useState(false);

  const [reloadTrigger, setReloadTrigger] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      try {
        const result = await fetchPaginatedData(prefix, tableName, page, 50, filterQuery, finalSearchTerm);
        setData(result.content);
        setTotalPages(result.totalPages);
      } catch (error) {
        console.error(`❌ Failed to fetch ${tableName}:`, error);
      }
    };

    loadData();
  }, [prefix, tableName, page, filterQuery, finalSearchTerm, reloadTrigger]);

  useEffect(() => {
    setSearchTerm('');
    setFinalSearchTerm('');
    setFilterQuery('');
    setPage(0);
    setActiveFilters({});
  }, [title]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFinalSearchTerm(searchTerm);
    setPage(0); // Reset to page 0 when new search is submitted
  };

  const handleFilterChange = (key, value) => {
    setPage(0);
    setActiveFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  useEffect(() => {
    const queryStr = Object.entries(activeFilters)
      .filter(([_, value]) => value !== '')
      .map(([key, value]) => `${key}:${value}`)
      .join(';');

    setFilterQuery(queryStr);
  }, [activeFilters]);

  const exportToCSV = () => {
    if (!data || data.length === 0) return;
  
    const csvRows = [];
  
    // Add header row
    const headers = columns.map(col => col.label);
    csvRows.push(headers.join(','));
  
    // Add data rows
    data.forEach(row => {
      const values = columns.map(col => {
        const value = row[col.key];
        if (value === null || value === undefined) return '';
        return typeof value === 'string' && value.includes(',')
          ? `"${value}"`  // Escape commas
          : value;
      });
      csvRows.push(values.join(','));
    });
  
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
  
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${tableName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="table-viewer-container">
      <div className="table-viewer-wrapper">
        <h2 className="table-viewer-title">{title.replace(/_/g, ' ')}</h2>

        <form onSubmit={handleSearch} className="table-viewer-searchbar">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>

        {filters.length > 0 && (
          <TableFilterBar
            filters={filters}
            currentFilters={activeFilters}
            onFilterChange={handleFilterChange}
          />
        )}

        {/* {creatable && (
          <div className="table-viewer-add-button-wrapper">
            <button
              onClick={() => setShowAddModal(true)}
              className="table-viewer-add-button"
            >
              + Add New
            </button>
          </div>
        )} */}

        
        <div className="table-viewer-button-bar">
          <div className="left-buttons">
            {creatable && (
              <button
                onClick={() => setShowAddModal(true)}
                className="table-viewer-add-button"
              >
                + Add New
              </button>
            )}
          </div>
          
          <div className="right-buttons">
            <button
              onClick={exportToCSV}
              className="table-viewer-export-button"
            >
              ⬇ Export to CSV
            </button>
          </div>
        </div>

        {/* {showAddModal && creatable && (
          <AddFormModal
            columns={columns}
            onAdd={async (newData) => {
              try {
                // await addTableRow(prefix, addEndpoint, newData);
                // setPage(0); // reload
                await addTableRow(prefix, addEndpoint, newData);
                setShowAddModal(false);
                setReloadTrigger((prev) => prev + 1);
              } catch (err) {
                alert("Failed to add entry.");
              }
            }}
            onClose={() => setShowAddModal(false)}
          />
        )} */}

        {showAddModal && creatable && (
          <AddFormModal
            columns={columns}
            onAdd={async (newData) => {
              try {
                let payload = newData;

                if (prefix === 'announcements') {
                  const toBool = (val, fallback = false) => {
                    if (typeof val === 'boolean') return val;
                    if (typeof val === 'string') {
                      const v = val.trim().toLowerCase();
                      if (v === 'true') return true;
                      if (v === 'false') return false;
                    }
                    return fallback;
                  };

                  const toInstantOrNull = (val) => {
                    if (!val) return null;
                    try {
                      return new Date(val).toISOString(); // Instant-compatible
                    } catch {
                      return null;
                    }
                  };

                  const platforms =
                    (newData.platformsCsv || '')
                      .split(',')
                      .map((s) => s.trim())
                      .filter((s) => s.length > 0);

                  payload = {
                    // ID is auto-generated → no need to send it
                    title: newData.title?.trim() || null,
                    message: newData.message?.trim(),
                    severity: (newData.severity || 'info').trim(),       // info|warning|critical

                    linkText: newData.linkText?.trim() || null,
                    linkUrl: newData.linkUrl?.trim() || null,
                    minVersion: newData.minVersion?.trim() || null,
                    maxVersion: newData.maxVersion?.trim() || null,

                    required: toBool(newData.required, false),
                    showOncePerVersion: toBool(newData.showOncePerVersion, false),
                    audience: (newData.audience || 'All').trim(),        // All|Student|Officer|Driver|Admin

                    startAt: toInstantOrNull(newData.startAt),
                    endAt: toInstantOrNull(newData.endAt),

                    platforms,                                           // List<String>
                    enabled: toBool(newData.enabled, true),
                    priority:
                      newData.priority !== undefined &&
                      newData.priority !== null &&
                      newData.priority !== ''
                        ? Number(newData.priority)
                        : 0,
                  };
                }

                await addTableRow(prefix, addEndpoint, payload);
                setShowAddModal(false);
                setReloadTrigger((prev) => prev + 1);
              } catch (err) {
                console.error('❌ Failed to add entry:', err);
                alert("Failed to add entry.");
              }
            }}
            onClose={() => setShowAddModal(false)}
          />
        )}



        <table className="table-viewer">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
              {deletable && <th></th>} {/* extra header cell for delete column */}
            </tr>
          </thead>
          {/* <tbody>
            {data.map((row, idx) => (
              <tr key={idx}>
                {columns.map((col) => (
                  <td key={col.key}>
                    {
                      typeof row[col.key] === 'boolean'
                        ? row[col.key] ? 'Yes' : 'No'
                        : row[col.key] !== null && row[col.key] !== undefined
                          ? col.format === 'date'
                            ? new Date(row[col.key]).toLocaleString()
                            : row[col.key]
                          : '—'
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody> */}
          <tbody>
            {data.map((row, idx) => (
              <tr key={idx}>
                {columns.map((col) => (
                  <td key={col.key}>
                    {
                      typeof row[col.key] === 'boolean'
                        ? row[col.key] ? 'Yes' : 'No'
                        : row[col.key] !== null && row[col.key] !== undefined
                          ? col.format === 'date'
                            ? new Date(row[col.key]).toLocaleString()
                            : row[col.key]
                          : '—'
                    }
                  </td>
                ))}

                {deletable && (
                  <td className="table-viewer-delete-cell">
                    <button
                      className="table-viewer-delete-button"
                      onClick={async () => {
                        const confirmDelete = window.confirm("Are you sure you want to delete this row?");
                        if (!confirmDelete) return;

                        try {
                          // await deleteTableRow(prefix, deleteEndpoint, row[columns[0].key]);
                          // setPage(0); // refresh table
                          await deleteTableRow(prefix, deleteEndpoint, row[columns[0].key]);
                          setReloadTrigger((prev) => prev + 1); // TRIGGER RELOAD
                        } catch (err) {
                          alert("Failed to delete.");
                        }
                      }}
                    >
                      Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="table-viewer-pagination">
          <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span>Page {page + 1} of {totalPages}</span>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
};

export default TableViewer;